import React, { useEffect, useMemo, useState } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { Badge } from '../../../ui/badge';
import EvidenceDrawer from '../../shared/EvidenceDrawer';
import SimulationPromptBanner from '../../shared/SimulationPromptBanner';
import ChatPanel from './RightPanel/ChatPanel';
import { ToolCall, AgentProgressEvent } from '../../../../hooks/useSimulationStream';
import { ChatMessage } from '../../../../types/case-workspace';

type SidebarTab = 'evidence' | 'activity' | 'chat';

interface CaseWorkspaceSidebarProps {
  signalId?: string;
  simulationId?: string;
  showSimulationPrompt: boolean;
  newEvidenceCount: number;
  currentStatus?: string | null;
  progressMessage?: string | null;
  activatedSkills?: string[] | null;
  toolCalls?: ToolCall[];
  agentProgress?: AgentProgressEvent[];
  isSimulationComplete: boolean;
  messages: ChatMessage[];
  inputMessage: string;
  isTyping: boolean;
  suggestedQuestions: string[];
  onRunSimulation: () => void;
  onDismissPrompt: () => void;
  onEvidenceUploaded: (evidenceId: string) => void;
  onEvidenceCompleted: (evidenceId: string) => void;
  onToggleEvidenceInReasoning: (evidenceId: string, used: boolean) => void;
  onNewEvidenceDetected: (hasNewEvidence: boolean) => void;
  onInputChange: (message: string) => void;
  onSendMessage: (message: string) => void;
  onDecisionClick?: (decisionIdOrReference: string) => void;
}

const CaseWorkspaceSidebar: React.FC<CaseWorkspaceSidebarProps> = ({
  signalId,
  simulationId,
  showSimulationPrompt,
  newEvidenceCount,
  currentStatus,
  progressMessage,
  activatedSkills,
  toolCalls = [],
  agentProgress = [],
  isSimulationComplete,
  messages,
  inputMessage,
  isTyping,
  suggestedQuestions,
  onRunSimulation,
  onDismissPrompt,
  onEvidenceUploaded,
  onEvidenceCompleted,
  onToggleEvidenceInReasoning,
  onNewEvidenceDetected,
  onInputChange,
  onSendMessage,
  onDecisionClick,
}) => {
  const [activeTab, setActiveTab] = useState<SidebarTab>('chat');

  useEffect(() => {
    if (!isSimulationComplete && activeTab === 'activity') {
      setActiveTab('chat');
    }
  }, [activeTab, isSimulationComplete]);

  const uniqueSkills = useMemo(() => {
    const seen = new Set<string>();
    return (activatedSkills || []).filter((skill) => {
      if (!skill || seen.has(skill)) {
        return false;
      }
      seen.add(skill);
      return true;
    });
  }, [activatedSkills]);

  const runNotes = useMemo(() => {
    const seen = new Set<string>();
    const notes = agentProgress.filter((event) => {
      const message = event.message?.trim();
      if (!message) {
        return false;
      }
      const key = `${event.phase}:${message}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    if (notes.length > 0) {
      return notes;
    }

    return toolCalls
      .filter((tool) => tool.status !== 'starting')
      .map((tool) => ({
        phase: 'tool_call',
        status: tool.status,
        message: tool.message || tool.name,
      }));
  }, [agentProgress, toolCalls]);

  const summarizedToolCalls = useMemo(() => {
    const seen = new Set<string>();

    return toolCalls.filter((tool) => {
      const toolName = tool.name?.trim();
      const message = tool.message?.trim();
      const isLowLevel = toolName === 'load_skill' || toolName === 'load_skill_reference';
      const isStarting = tool.status === 'starting';
      const key = `${toolName}:${message}`;

      if (!message || isLowLevel || isStarting || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }, [toolCalls]);

  const totalActivitySteps = uniqueSkills.length + runNotes.length + summarizedToolCalls.length;

  return (
    <aside className="sticky top-6 w-[340px] shrink-0 self-start">
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-[var(--shadow-card)]">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as SidebarTab)} className="flex h-[calc(100vh-10rem)] flex-col">
          <TabsList className="m-3 mb-0 grid h-auto grid-cols-3">
            <TabsTrigger value="evidence">Evidence</TabsTrigger>
            <TabsTrigger value="chat">Ask Juno</TabsTrigger>
            <TabsTrigger value="activity" disabled={!isSimulationComplete}>
              Activity
            </TabsTrigger>
          </TabsList>

          <TabsContent value="evidence" className="mt-0 flex-1 overflow-hidden p-3">
            {signalId ? (
              <div className="flex h-full flex-col overflow-hidden">
                {showSimulationPrompt && (
                  <div className="mb-3">
                    <SimulationPromptBanner
                      onRunSimulation={onRunSimulation}
                      onDismiss={onDismissPrompt}
                      evidenceCount={newEvidenceCount}
                    />
                  </div>
                )}
                <EvidenceDrawer
                  signalId={signalId}
                  orientation="vertical"
                  simulationId={simulationId}
                  onEvidenceUploaded={onEvidenceUploaded}
                  onEvidenceCompleted={onEvidenceCompleted}
                  onToggleUsedInReasoning={onToggleEvidenceInReasoning}
                  onNewEvidenceDetected={onNewEvidenceDetected}
                  className="min-h-0"
                />
              </div>
            ) : (
              <div className="flex h-full items-center justify-center text-center text-[13px] text-muted-foreground">
                Evidence will appear once the record is available.
              </div>
            )}
          </TabsContent>

          <TabsContent value="activity" className="mt-0 flex-1 overflow-y-auto p-4">
            {!isSimulationComplete ? (
              <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 px-6 text-center">
                <div>
                  <div className="mb-2 text-sm font-medium text-foreground">Activity will appear once guidance is complete</div>
                  <div className="text-[12px] leading-[1.6] text-muted-foreground">
                    Live runs stay focused in Ask Juno. The full step-by-step activity record becomes available after the simulation finishes.
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                      Agent activity
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {totalActivitySteps} step{totalActivitySteps === 1 ? '' : 's'}
                    </div>
                  </div>

                  {uniqueSkills.length > 0 && (
                    <div className="mb-4">
                      <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Skills loaded
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {uniqueSkills.map((skill) => (
                          <Badge key={skill} variant="outline">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {runNotes.length > 0 && (
                    <div className="mb-4">
                      <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Run notes
                      </div>
                      <div className="space-y-2">
                        {runNotes.map((event, index) => (
                          <div key={`progress-${index}`} className="flex items-start gap-3 text-[12px] leading-[1.6]">
                            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                            <div className="min-w-0">
                              <div className="font-medium text-foreground">{event.message}</div>
                              {event.phase && event.phase !== 'analysis' && event.phase !== 'tool_call' && (
                                <div className="text-[11px] text-muted-foreground">{event.phase.replace(/_/g, ' ')}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {summarizedToolCalls.length > 0 && (
                    <div>
                      <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Tool calls
                      </div>
                      <div className="space-y-2">
                        {summarizedToolCalls.map((tool, index) => (
                          <div key={`tool-${index}`} className="flex items-start gap-3 text-[12px] leading-[1.6]">
                            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-muted-foreground/45" />
                            <div className="min-w-0">
                              <div className="text-foreground">{tool.message || tool.name}</div>
                              <div className="text-[11px] text-muted-foreground">Tool call</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {totalActivitySteps === 0 && (
                    <div className="text-[12px] text-muted-foreground">
                      Activity will appear here once the guidance run completes.
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="chat" className="mt-0 flex-1 overflow-hidden">
            <ChatPanel
              messages={messages}
              inputMessage={inputMessage}
              isTyping={isTyping}
              suggestedQuestions={suggestedQuestions}
              currentStatus={currentStatus}
              progressMessage={progressMessage}
              activatedSkills={activatedSkills}
              toolCalls={toolCalls}
              agentProgress={agentProgress}
              isSimulationComplete={isSimulationComplete}
              onInputChange={onInputChange}
              onSendMessage={onSendMessage}
              isOpen={true}
              onClose={() => {}}
              onDecisionClick={onDecisionClick}
              showHeader={false}
            />
          </TabsContent>
        </Tabs>
      </div>
    </aside>
  );
};

export default CaseWorkspaceSidebar;
